import type { CatalogItem, ResizeAxis, SceneDocument, SceneElement, Vec2, Vec3, Wall } from "@calcplan/domain";
import { resolveSnap, type SnapKind } from "@calcplan/geometry";
import {
  renderedElementCenterMm,
  renderedElementSizeMm,
  renderedElementVerticalIntervalMm,
} from "./rendered-envelope";

export interface CatalogPlacement {
  positionMm: Vec3;
  rawPositionMm: Vec3;
  snapKind: SnapKind;
}

export type PlacementSurfaceKind = "floor" | "wall";

export interface PlacementSurfaceConstraint {
  kind: PlacementSurfaceKind;
  pointMm: Vec3;
  normal: Vec3;
  wallId?: string;
}

export interface SurfacePlacement extends CatalogPlacement {
  rotationRad: Vec3;
  surfaceKind: PlacementSurfaceKind;
}

export interface SurfacePlacementOptions {
  alignRotationToSurface?: boolean;
  snap?: boolean;
}

export type WallMountFace = "interior" | "exterior";

export interface WallMountSurfaceOptions {
  face?: WallMountFace;
  roomId?: string;
}

export interface WallMountSurfaceResolution {
  surface: PlacementSurfaceConstraint;
  face: WallMountFace;
  referenceRoomId: string | null;
  adjacentRoomIds: string[];
}

/**
 * Chooses a wall face from room geometry, rather than trusting which side an
 * approximate point happened to land on. A stale/exterior coordinate must not
 * silently put agent-positioned cabinetry outside the room.
 */
export function resolveWallMountSurface(
  scene: SceneDocument,
  wall: Wall,
  preferredCenterMm: Vec3,
  options: WallMountSurfaceOptions = {},
): WallMountSurfaceResolution {
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const length = Math.hypot(dx, dz);
  if (length < 1) throw new Error(`A parede ${wall.id} não tem comprimento suficiente para fixar o módulo.`);

  const midpoint: Vec3 = [
    (wall.startMm[0] + wall.endMm[0]) / 2,
    preferredCenterMm[1],
    (wall.startMm[1] + wall.endMm[1]) / 2,
  ];
  const baseNormal: Vec3 = [-dz / length, 0, dx / length];
  const probeDistance = Math.max(10, Math.max(1, wall.thicknessMm) / 2 + 5);
  const roomsOnSide = (sign: 1 | -1): string[] => {
    const probe: Vec2 = [
      midpoint[0] + baseNormal[0] * probeDistance * sign,
      midpoint[2] + baseNormal[2] * probeDistance * sign,
    ];
    return scene.rooms
      .filter((room) => pointInsidePolygon(probe, room.boundary)
        && !room.holes.some((hole) => pointInsidePolygon(probe, hole)))
      .map((room) => room.id);
  };

  const positiveRoomIds = roomsOnSide(1);
  const negativeRoomIds = roomsOnSide(-1);
  let interiorSign: 1 | -1;
  let referenceRoomId: string | null = null;

  if (options.roomId) {
    const positive = positiveRoomIds.includes(options.roomId);
    const negative = negativeRoomIds.includes(options.roomId);
    if (!positive && !negative) {
      throw new Error(`O ambiente ${options.roomId} não é adjacente à parede ${wall.id}. Leia a arquitetura novamente e informe a parede ou o ambiente correto.`);
    }
    interiorSign = positive && !negative
      ? 1
      : negative && !positive
        ? -1
        : preferredSide(preferredCenterMm, midpoint, baseNormal);
    referenceRoomId = options.roomId;
  } else if (positiveRoomIds.length > 0 && negativeRoomIds.length === 0) {
    interiorSign = 1;
    referenceRoomId = positiveRoomIds[0] ?? null;
  } else if (negativeRoomIds.length > 0 && positiveRoomIds.length === 0) {
    interiorSign = -1;
    referenceRoomId = negativeRoomIds[0] ?? null;
  } else if (positiveRoomIds.length > 0 || negativeRoomIds.length > 0) {
    interiorSign = preferredSide(preferredCenterMm, midpoint, baseNormal);
    const preferredRooms = interiorSign === 1 ? positiveRoomIds : negativeRoomIds;
    referenceRoomId = preferredRooms[0] ?? positiveRoomIds[0] ?? negativeRoomIds[0] ?? null;
  } else {
    // Free/legacy walls may not have a derived adjacent room. Keep a stable
    // fallback, preferring the overall room geometry before the supplied point.
    const roomPoints = scene.rooms.flatMap((room) => room.boundary);
    if (roomPoints.length) {
      const roomReference: Vec3 = [
        roomPoints.reduce((sum, point) => sum + point[0], 0) / roomPoints.length,
        preferredCenterMm[1],
        roomPoints.reduce((sum, point) => sum + point[1], 0) / roomPoints.length,
      ];
      interiorSign = preferredSide(roomReference, midpoint, baseNormal);
    } else {
      interiorSign = preferredSide(preferredCenterMm, midpoint, baseNormal);
    }
  }

  const face = options.face ?? "interior";
  const requestedSign = face === "interior" ? interiorSign : interiorSign === 1 ? -1 : 1;
  const normal: Vec3 = [baseNormal[0] * requestedSign, 0, baseNormal[2] * requestedSign];
  const halfThickness = Math.max(1, wall.thicknessMm) / 2;
  return {
    surface: {
      kind: "wall",
      wallId: wall.id,
      normal,
      pointMm: [
        wall.startMm[0] + normal[0] * halfThickness,
        0,
        wall.startMm[1] + normal[2] * halfThickness,
      ],
    },
    face,
    referenceRoomId,
    adjacentRoomIds: requestedSign === 1 ? positiveRoomIds : negativeRoomIds,
  };
}

function preferredSide(point: Vec3, midpoint: Vec3, baseNormal: Vec3): 1 | -1 {
  const signedDistance =
    (point[0] - midpoint[0]) * baseNormal[0]
    + (point[2] - midpoint[2]) * baseNormal[2];
  return signedDistance < 0 ? -1 : 1;
}

function pointInsidePolygon(point: Vec2, polygon: Vec2[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current, current += 1) {
    const a = polygon[current]!;
    const b = polygon[previous]!;
    if (pointOnSegment(point, a, b)) return true;
    const crosses = (a[1] > point[1]) !== (b[1] > point[1])
      && point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0];
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointOnSegment(point: Vec2, start: Vec2, end: Vec2): boolean {
  const cross = (point[0] - start[0]) * (end[1] - start[1])
    - (point[1] - start[1]) * (end[0] - start[0]);
  if (Math.abs(cross) > 0.5) return false;
  const dot = (point[0] - start[0]) * (end[0] - start[0])
    + (point[1] - start[1]) * (end[1] - start[1]);
  if (dot < 0) return false;
  const squaredLength = (end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2;
  return dot <= squaredLength;
}

export interface ElementResize {
  dimensionsMm: SceneElement["dimensionsMm"];
  translationMm: Vec3;
}

export type ResizeCollisionKind = PlacementCollisionKind | "ceiling";

export interface CollisionConstrainedResize extends ElementResize {
  collisionKind: ResizeCollisionKind | null;
  collisionId: string | null;
}

export interface ResizeCollisionConstraintOptions {
  surface?: PlacementSurfaceConstraint | null;
  bypass?: boolean;
}

/** Smaller than this a face has passed through its opposite one. */
export const MIN_DIMENSION_MM = 10;

export type PlacementCollisionKind = "wall" | "element";

export interface CollisionConstrainedPlacement {
  positionMm: Vec3;
  collisionKind: PlacementCollisionKind | null;
  collisionId: string | null;
}

export interface CollisionConstraintOptions {
  bypass?: boolean;
  ignoreWallId?: string;
}

export const PLACEMENT_SURFACE_EXTENSION_KEY = "calcplanPlacementSurface";

interface StoredPlacementSurface {
  version: 1;
  kind: PlacementSurfaceKind;
  pointYmm?: number;
  normal?: Vec3;
  wallId?: string;
}

export function readElementPlacementSurface(
  scene: SceneDocument,
  element: SceneElement,
): PlacementSurfaceConstraint | null {
  const stored = element.extensions[PLACEMENT_SURFACE_EXTENSION_KEY];
  if (!isRecord(stored) || stored.version !== 1) return null;

  if (stored.kind === "floor") {
    const pointYmm = Number(stored.pointYmm);
    return {
      kind: "floor",
      pointMm: [0, Number.isFinite(pointYmm) ? pointYmm : 0, 0],
      normal: [0, 1, 0],
    };
  }

  if (stored.kind !== "wall" || typeof stored.wallId !== "string") return null;
  const wall = scene.walls.find((candidate) => candidate.id === stored.wallId);
  if (!wall) return null;
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const length = Math.hypot(dx, dz);
  if (length < 1) return null;

  const wallNormal: Vec3 = [-dz / length, 0, dx / length];
  const storedNormal = finiteVec3(stored.normal);
  const side =
    storedNormal &&
    storedNormal[0] * wallNormal[0] + storedNormal[2] * wallNormal[2] < 0
      ? -1
      : 1;
  const normal: Vec3 = [wallNormal[0] * side, 0, wallNormal[2] * side];
  const halfThickness = Math.max(1, wall.thicknessMm) / 2;
  return {
    kind: "wall",
    wallId: wall.id,
    normal,
    pointMm: [
      wall.startMm[0] + normal[0] * halfThickness,
      0,
      wall.startMm[1] + normal[2] * halfThickness,
    ],
  };
}

/**
 * Resolves the wall a component belongs to. New documents use the persisted
 * placement surface; older scenes are recovered by detecting an actual contact
 * between the component footprint and a wall face.
 */
export function resolveElementLinkedWallId(
  scene: SceneDocument,
  element: SceneElement,
  contactToleranceMm = 12,
): string | null {
  const storedSurface = readElementPlacementSurface(scene, element);
  if (storedSurface?.kind === "wall" && storedSurface.wallId) {
    return storedSurface.wallId;
  }

  if (!element.visible) return null;
  const [widthMm, , depthMm] = renderedElementSizeMm(element);
  const physicalCenter = renderedElementCenterMm(element);
  const yawRad = Number.isFinite(element.transform.rotationRad[1])
    ? element.transform.rotationRad[1]
    : 0;
  const widthAxis: [number, number] = [Math.cos(yawRad), -Math.sin(yawRad)];
  const depthAxis: [number, number] = [Math.sin(yawRad), Math.cos(yawRad)];
  const [elementBottomMm, elementTopMm] = renderedElementVerticalIntervalMm(element);
  let closest: { wallId: string; gapMm: number } | null = null;

  for (const wall of scene.walls) {
    if (!verticalIntervalsOverlap(elementBottomMm, elementTopMm, 0, wall.heightMm)) {
      continue;
    }
    const dx = wall.endMm[0] - wall.startMm[0];
    const dz = wall.endMm[1] - wall.startMm[1];
    const length = Math.hypot(dx, dz);
    if (length < 1) continue;

    const tangent: [number, number] = [dx / length, dz / length];
    const normal: [number, number] = [-tangent[1], tangent[0]];
    const centerFromStart: [number, number] = [
      physicalCenter[0] - wall.startMm[0],
      physicalCenter[2] - wall.startMm[1],
    ];
    const halfAlongWall =
      widthMm / 2 * Math.abs(dot2(widthAxis, tangent)) +
      depthMm / 2 * Math.abs(dot2(depthAxis, tangent));
    const centerAlongWall = dot2(centerFromStart, tangent);
    if (
      centerAlongWall + halfAlongWall < -contactToleranceMm ||
      centerAlongWall - halfAlongWall > length + contactToleranceMm
    ) {
      continue;
    }

    const halfTowardWall =
      widthMm / 2 * Math.abs(dot2(widthAxis, normal)) +
      depthMm / 2 * Math.abs(dot2(depthAxis, normal));
    const centerDistance = Math.abs(dot2(centerFromStart, normal));
    const gapMm =
      centerDistance -
      (Math.max(1, wall.thicknessMm) / 2 + halfTowardWall);
    if (Math.abs(gapMm) > Math.max(0, contactToleranceMm)) continue;
    if (!closest || Math.abs(gapMm) < Math.abs(closest.gapMm)) {
      closest = { wallId: wall.id, gapMm };
    }
  }

  return closest?.wallId ?? null;
}

export function withElementPlacementSurface(
  element: SceneElement,
  surface: PlacementSurfaceConstraint | null,
): Record<string, unknown> {
  const extensions = { ...element.extensions };
  if (!surface) {
    delete extensions[PLACEMENT_SURFACE_EXTENSION_KEY];
    return extensions;
  }

  const stored: StoredPlacementSurface =
    surface.kind === "wall"
      ? {
          version: 1,
          kind: "wall",
          wallId: surface.wallId,
          normal: [...surface.normal],
        }
      : {
          version: 1,
          kind: "floor",
          pointYmm: finiteMillimeter(surface.pointMm[1]),
        };
  extensions[PLACEMENT_SURFACE_EXTENSION_KEY] = stored;
  return extensions;
}

export function placementSurfacesEqual(
  left: PlacementSurfaceConstraint | null,
  right: PlacementSurfaceConstraint | null,
): boolean {
  if (!left || !right) return left === right;
  if (left.kind !== right.kind) return false;
  if (left.kind === "floor") {
    return Math.abs(left.pointMm[1] - right.pointMm[1]) < 0.5;
  }
  if (left.wallId !== right.wallId) return false;
  const leftNormal = normalizeHorizontal(left.normal);
  const rightNormal = normalizeHorizontal(right.normal);
  return (
    leftNormal[0] * rightNormal[0] + leftNormal[2] * rightNormal[2] > 0.999
  );
}

export function resolveCatalogPlacement(
  scene: SceneDocument,
  surfacePointMm: Vec3,
  itemHeightMm: number,
): CatalogPlacement {
  const rawX = finiteMillimeter(surfacePointMm[0]);
  const surfaceY = finiteMillimeter(surfacePointMm[1]);
  const rawZ = finiteMillimeter(surfacePointMm[2]);
  const snapRadiusMm = Math.max(40, Math.min(150, scene.settings.gridSizeMm * 0.2));
  const snap = resolveSnap(scene, [rawX, rawZ], { radiusMm: snapRadiusMm });

  return {
    rawPositionMm: [rawX, surfaceY, rawZ],
    positionMm: [
      finiteMillimeter(snap.pointMm[0]),
      finiteMillimeter(surfaceY + Math.max(10, itemHeightMm) / 2),
      finiteMillimeter(snap.pointMm[1]),
    ],
    snapKind: snap.kind,
  };
}

export function resolveElementMove(
  scene: SceneDocument,
  pointerSurfaceMm: Vec3,
  pointerOffsetMm: [number, number],
  currentYmm: number,
  elementHeightMm: number,
): CatalogPlacement {
  const placement = resolveCatalogPlacement(
    scene,
    [
      pointerSurfaceMm[0] + pointerOffsetMm[0],
      pointerSurfaceMm[1],
      pointerSurfaceMm[2] + pointerOffsetMm[1],
    ],
    elementHeightMm,
  );

  return {
    ...placement,
    positionMm: [
      placement.positionMm[0],
      finiteMillimeter(currentYmm),
      placement.positionMm[2],
    ],
  };
}

/**
 * Sweeps a component between two positions and stops it at the first wall or
 * component it would cross. Passing `bypass` mirrors Metriplan's Ctrl gesture.
 */
export function resolveCollisionConstrainedMove(
  scene: SceneDocument,
  elementId: string,
  startPositionMm: Vec3,
  targetPositionMm: Vec3,
  renderedSizeMm: Vec3,
  rotationRad: Vec3,
  options: CollisionConstraintOptions = {},
): CollisionConstrainedPlacement {
  if (options.bypass) {
    return {
      positionMm: [...targetPositionMm],
      collisionKind: null,
      collisionId: null,
    };
  }

  const ignoredAtStart = options.ignoreWallId
    ? new Set([options.ignoreWallId])
    : EMPTY_IGNORE;
  const direct = sweepCollisionConstrainedMove(
    scene,
    elementId,
    startPositionMm,
    targetPositionMm,
    renderedSizeMm,
    rotationRad,
    ignoredAtStart,
  );
  if (!direct.collisionKind || !direct.collisionId) return direct;

  const remaining: Vec3 = [
    targetPositionMm[0] - direct.positionMm[0],
    targetPositionMm[1] - direct.positionMm[1],
    targetPositionMm[2] - direct.positionMm[2],
  ];
  const tangent = collisionSlideTangent(
    scene,
    direct.collisionKind,
    direct.collisionId,
    direct.positionMm,
    renderedSizeMm,
    rotationRad,
  );
  if (!tangent) return direct;
  const distanceAlongObstacle = remaining[0] * tangent[0] + remaining[2] * tangent[1];
  if (Math.abs(distanceAlongObstacle) < 0.1 && Math.abs(remaining[1]) < 0.1) return direct;

  const slideTarget: Vec3 = [
    direct.positionMm[0] + tangent[0] * distanceAlongObstacle,
    targetPositionMm[1],
    direct.positionMm[2] + tangent[1] * distanceAlongObstacle,
  ];
  const ignoredDuringSlide = new Set(ignoredAtStart);
  ignoredDuringSlide.add(direct.collisionId);
  const slide = sweepCollisionConstrainedMove(
    scene,
    elementId,
    direct.positionMm,
    slideTarget,
    renderedSizeMm,
    rotationRad,
    ignoredDuringSlide,
  );
  return slide.collisionKind
    ? slide
    : {
        positionMm: slide.positionMm,
        collisionKind: direct.collisionKind,
        collisionId: direct.collisionId,
      };
}

/**
 * Returns the edge direction that remains free after hitting an obstacle. A
 * wall has one obvious tangent; for a component, the closest face of its OBB
 * decides which local edge the moving component should follow.
 */
function collisionSlideTangent(
  scene: SceneDocument,
  collisionKind: PlacementCollisionKind,
  collisionId: string,
  contactPositionMm: Vec3,
  renderedSizeMm: Vec3,
  rotationRad: Vec3,
): [number, number] | null {
  if (collisionKind === "wall") {
    const wall = scene.walls.find((candidate) => candidate.id === collisionId);
    if (!wall) return null;
    const dx = wall.endMm[0] - wall.startMm[0];
    const dz = wall.endMm[1] - wall.startMm[1];
    const length = Math.hypot(dx, dz);
    return length < 1 ? null : [dx / length, dz / length];
  }

  const obstacleElement = scene.elements.find((candidate) => candidate.id === collisionId);
  if (!obstacleElement) return null;
  const obstacleSizeMm = renderedElementSizeMm(obstacleElement);
  const obstacleCenterMm = renderedElementCenterMm(obstacleElement);
  const obstacleVertical = renderedElementVerticalIntervalMm(obstacleElement);
  const moving = createHorizontalObb(
    [contactPositionMm[0], contactPositionMm[2]],
    renderedSizeMm[0],
    renderedSizeMm[2],
    rotationRad[1],
    contactPositionMm[1] - renderedSizeMm[1] / 2,
    contactPositionMm[1] + renderedSizeMm[1] / 2,
  );
  const obstacle = createHorizontalObb(
    [obstacleCenterMm[0], obstacleCenterMm[2]],
    obstacleSizeMm[0],
    obstacleSizeMm[2],
    obstacleElement.transform.rotationRad[1],
    obstacleVertical[0],
    obstacleVertical[1],
  );
  const centerDelta: [number, number] = [
    moving.center[0] - obstacle.center[0],
    moving.center[1] - obstacle.center[1],
  ];
  const faceScore = obstacle.axes.map((axis, index) => {
    const reach = obstacle.halfSize[index]! + horizontalObbProjectionRadius(moving, axis);
    return Math.abs(dot2(centerDelta, axis)) / Math.max(1, reach);
  });
  const contactAxisIndex = faceScore[0]! >= faceScore[1]! ? 0 : 1;
  return obstacle.axes[contactAxisIndex === 0 ? 1 : 0];
}

function sweepCollisionConstrainedMove(
  scene: SceneDocument,
  elementId: string,
  startPositionMm: Vec3,
  targetPositionMm: Vec3,
  renderedSizeMm: Vec3,
  rotationRad: Vec3,
  ignore: ReadonlySet<string> = EMPTY_IGNORE,
): CollisionConstrainedPlacement {
  const delta: Vec3 = [
    targetPositionMm[0] - startPositionMm[0],
    targetPositionMm[1] - startPositionMm[1],
    targetPositionMm[2] - startPositionMm[2],
  ];
  const distanceMm = Math.hypot(...delta);
  if (distanceMm < 0.1) {
    return {
      positionMm: [...targetPositionMm],
      collisionKind: null,
      collisionId: null,
    };
  }

  const smallestHorizontalSize = Math.max(
    20,
    Math.min(Math.abs(renderedSizeMm[0]), Math.abs(renderedSizeMm[2])),
  );
  const sampleDistanceMm = Math.max(20, Math.min(100, smallestHorizontalSize / 4));
  const steps = Math.max(1, Math.min(320, Math.ceil(distanceMm / sampleDistanceMm)));
  let previousT = 0;

  for (let step = 1; step <= steps; step += 1) {
    const t = step / steps;
    const candidate = interpolatePosition(startPositionMm, targetPositionMm, t);
    const collision = findPlacementCollision(
      scene,
      elementId,
      candidate,
      renderedSizeMm,
      rotationRad,
      ignore,
    );
    if (!collision) {
      previousT = t;
      continue;
    }

    let lower = previousT;
    let upper = t;
    for (let iteration = 0; iteration < 18; iteration += 1) {
      const middle = (lower + upper) / 2;
      const middlePosition = interpolatePosition(startPositionMm, targetPositionMm, middle);
      if (
        findPlacementCollision(
          scene,
          elementId,
          middlePosition,
          renderedSizeMm,
          rotationRad,
          ignore,
        )
      ) {
        upper = middle;
      } else {
        lower = middle;
      }
    }

    return {
      positionMm: interpolatePosition(startPositionMm, targetPositionMm, lower),
      collisionKind: collision.kind,
      collisionId: collision.id,
    };
  }

  return {
    positionMm: [...targetPositionMm],
    collisionKind: null,
    collisionId: null,
  };
}

/**
 * Places a component on a surface selected during an active drag. Floors keep
 * the base supported; walls align the component depth to the picked face and
 * constrain its center to the usable wall rectangle.
 */
export function resolveSurfacePlacement(
  scene: SceneDocument,
  pointerSurfaceMm: Vec3,
  surface: PlacementSurfaceConstraint,
  renderedSizeMm: Vec3,
  currentRotationRad: Vec3,
  options: SurfacePlacementOptions = {},
): SurfacePlacement {
  if (surface.kind === "floor") {
    const placement = options.snap === false
      ? {
          rawPositionMm: [
            finiteMillimeter(pointerSurfaceMm[0]),
            finiteMillimeter(surface.pointMm[1]),
            finiteMillimeter(pointerSurfaceMm[2]),
          ] as Vec3,
          positionMm: [
            finiteMillimeter(pointerSurfaceMm[0]),
            finiteMillimeter(surface.pointMm[1] + Math.max(10, renderedSizeMm[1]) / 2),
            finiteMillimeter(pointerSurfaceMm[2]),
          ] as Vec3,
          snapKind: "none" as const,
        }
      : resolveCatalogPlacement(
          scene,
          [pointerSurfaceMm[0], surface.pointMm[1], pointerSurfaceMm[2]],
          renderedSizeMm[1],
        );
    return {
      ...placement,
      rotationRad: [0, finiteAngle(currentRotationRad[1]), 0],
      surfaceKind: "floor",
    };
  }

  const normal = normalizeHorizontal(surface.normal);
  const projectedPointer = projectPointToPlane(pointerSurfaceMm, surface.pointMm, normal);
  const snapRadiusMm = Math.max(40, Math.min(150, scene.settings.gridSizeMm * 0.2));
  const snap = options.snap === false
    ? { pointMm: [projectedPointer[0], projectedPointer[2]] as [number, number], kind: "none" as const }
    : resolveSnap(scene, [projectedPointer[0], projectedPointer[2]], {
        radiusMm: snapRadiusMm,
      });
  const snappedOnPlane = projectPointToPlane(
    [snap.pointMm[0], projectedPointer[1], snap.pointMm[1]],
    surface.pointMm,
    normal,
  );
  const wall = surface.wallId
    ? scene.walls.find((candidate) => candidate.id === surface.wallId)
    : undefined;
  const wallAlignedRotationY = finiteAngle(Math.atan2(normal[0], normal[2]));
  const rotationY = options.alignRotationToSurface === false
    ? finiteAngle(currentRotationRad[1])
    : wallAlignedRotationY;
  const widthAxis: Vec3 = [Math.cos(rotationY), 0, -Math.sin(rotationY)];
  const depthAxis: Vec3 = [Math.sin(rotationY), 0, Math.cos(rotationY)];
  const halfWidth = Math.max(5, renderedSizeMm[0] / 2);
  const halfDepth = Math.max(5, renderedSizeMm[2] / 2);
  const supportDistance =
    halfWidth * Math.abs(dotHorizontal(widthAxis, normal))
    + halfDepth * Math.abs(dotHorizontal(depthAxis, normal));
  let contactPoint = snappedOnPlane;

  if (wall) {
    const wallDx = wall.endMm[0] - wall.startMm[0];
    const wallDz = wall.endMm[1] - wall.startMm[1];
    const wallLength = Math.hypot(wallDx, wallDz);
    if (wallLength > 1e-6) {
      const wallTangent: Vec3 = [wallDx / wallLength, 0, wallDz / wallLength];
      const distanceAlongWall =
        (contactPoint[0] - wall.startMm[0]) * wallTangent[0] +
        (contactPoint[2] - wall.startMm[1]) * wallTangent[2];
      const halfAlongWall =
        halfWidth * Math.abs(dotHorizontal(widthAxis, wallTangent))
        + halfDepth * Math.abs(dotHorizontal(depthAxis, wallTangent));
      const minimum = Math.min(halfAlongWall, wallLength / 2);
      const maximum = Math.max(minimum, wallLength - halfAlongWall);
      const clampedDistance = Math.min(maximum, Math.max(minimum, distanceAlongWall));
      const correction = clampedDistance - distanceAlongWall;
      contactPoint = [
        contactPoint[0] + wallTangent[0] * correction,
        contactPoint[1],
        contactPoint[2] + wallTangent[2] * correction,
      ];
    }

    const halfHeight = Math.max(5, renderedSizeMm[1] / 2);
    const minimumY = Math.min(halfHeight, wall.heightMm / 2);
    const maximumY = Math.max(minimumY, wall.heightMm - halfHeight);
    contactPoint[1] = Math.min(maximumY, Math.max(minimumY, contactPoint[1]));
  }

  return {
    rawPositionMm: [
      finiteMillimeter(projectedPointer[0]),
      finiteMillimeter(projectedPointer[1]),
      finiteMillimeter(projectedPointer[2]),
    ],
    positionMm: [
      finiteMillimeter(contactPoint[0] + normal[0] * supportDistance),
      finiteMillimeter(contactPoint[1]),
      finiteMillimeter(contactPoint[2] + normal[2] * supportDistance),
    ],
    rotationRad: [0, rotationY, 0],
    snapKind: snap.kind,
    surfaceKind: "wall",
  };
}

/**
 * Rotates a component without breaking its persisted support. For a wall, the
 * center is moved by the oriented footprint extent so the closest corner or
 * edge remains flush with the same wall face throughout the gesture.
 */
export function resolveSurfaceRotation(
  scene: SceneDocument,
  currentPositionMm: Vec3,
  surface: PlacementSurfaceConstraint,
  renderedSizeMm: Vec3,
  nextRotationRad: Vec3,
): SurfacePlacement {
  const normal = normalizeAxis(
    surface.normal,
    surface.kind === "floor" ? [0, 1, 0] : [0, 0, 1],
  );
  const contactPoint = projectPointToPlane(currentPositionMm, surface.pointMm, normal);
  return resolveSurfacePlacement(
    scene,
    contactPoint,
    surface,
    renderedSizeMm,
    nextRotationRad,
    { alignRotationToSurface: false, snap: false },
  );
}

export function intersectRayWithPlacementSurface(
  rayOriginMm: Vec3,
  rayDirection: Vec3,
  surface: PlacementSurfaceConstraint,
): Vec3 | null {
  return intersectRayWithPlane(
    rayOriginMm,
    rayDirection,
    surface.pointMm,
    surface.normal,
    surface.kind === "floor" ? [0, 1, 0] : [0, 0, 1],
  );
}

export function intersectRayWithPlane(
  rayOriginMm: Vec3,
  rayDirection: Vec3,
  planePointMm: Vec3,
  planeNormal: Vec3,
  fallbackNormal: Vec3 = [0, 1, 0],
): Vec3 | null {
  const normal = normalizeAxis(planeNormal, fallbackNormal);
  const denominator =
    rayDirection[0] * normal[0] +
    rayDirection[1] * normal[1] +
    rayDirection[2] * normal[2];
  if (Math.abs(denominator) < 1e-8) return null;
  const distance =
    ((planePointMm[0] - rayOriginMm[0]) * normal[0] +
      (planePointMm[1] - rayOriginMm[1]) * normal[1] +
      (planePointMm[2] - rayOriginMm[2]) * normal[2]) /
    denominator;
  if (!Number.isFinite(distance) || distance < 0) return null;
  return [
    finiteMillimeter(rayOriginMm[0] + rayDirection[0] * distance),
    finiteMillimeter(rayOriginMm[1] + rayDirection[1] * distance),
    finiteMillimeter(rayOriginMm[2] + rayDirection[2] * distance),
  ];
}

/**
 * Converts the pointer movement captured on a stable drag plane into motion
 * along the component's support surface. The viewport uses a plane parallel
 * to the floor or wall through the exact grabbed point, which keeps that point
 * under the cursor while this projection preserves contact with its support.
 */
export function resolveStablePlacementSurfaceDragPoint(
  positionAtDragStartMm: Vec3,
  pointerAtDragStartMm: Vec3,
  currentPointerMm: Vec3,
  surface: PlacementSurfaceConstraint,
  renderedSizeMm: Vec3,
): Vec3 {
  const normal = normalizeAxis(
    surface.normal,
    surface.kind === "floor" ? [0, 1, 0] : [0, 0, 1],
  );
  const supportDistance = surface.kind === "floor"
    ? Math.max(5, renderedSizeMm[1] / 2)
    : Math.max(5, renderedSizeMm[2] / 2);
  const startContact = projectPointToPlane(
    [
      positionAtDragStartMm[0] - normal[0] * supportDistance,
      positionAtDragStartMm[1] - normal[1] * supportDistance,
      positionAtDragStartMm[2] - normal[2] * supportDistance,
    ],
    surface.pointMm,
    normal,
  );
  const delta: Vec3 = [
    currentPointerMm[0] - pointerAtDragStartMm[0],
    currentPointerMm[1] - pointerAtDragStartMm[1],
    currentPointerMm[2] - pointerAtDragStartMm[2],
  ];
  const normalDelta =
    delta[0] * normal[0] + delta[1] * normal[1] + delta[2] * normal[2];
  return [
    finiteMillimeter(startContact[0] + delta[0] - normal[0] * normalDelta),
    finiteMillimeter(startContact[1] + delta[1] - normal[1] * normalDelta),
    finiteMillimeter(startContact[2] + delta[2] - normal[2] * normalDelta),
  ];
}

/**
 * Keeps the exact point grabbed by the cursor stable when a component is
 * constrained to a floor or wall. The offset is calculated before snapping and
 * boundary clamping; otherwise grabbing a face near a wall edge can collapse
 * the offset and make the component jump to its center.
 */
export function resolvePlacementSurfaceDragOffset(
  currentPositionMm: Vec3,
  pointerSurfaceMm: Vec3,
  surface: PlacementSurfaceConstraint,
  renderedSizeMm: Vec3,
): Vec3 {
  const normal = normalizeAxis(
    surface.normal,
    surface.kind === "floor" ? [0, 1, 0] : [0, 0, 1],
  );
  const pointerOnSurface = projectPointToPlane(pointerSurfaceMm, surface.pointMm, normal);
  const supportDistance = surface.kind === "floor"
    ? Math.max(5, renderedSizeMm[1] / 2)
    : Math.max(5, renderedSizeMm[2] / 2);
  return [
    currentPositionMm[0] - normal[0] * supportDistance - pointerOnSurface[0],
    currentPositionMm[1] - normal[1] * supportDistance - pointerOnSurface[1],
    currentPositionMm[2] - normal[2] * supportDistance - pointerOnSurface[2],
  ];
}

export function applyPlacementSurfaceDragOffset(
  pointerSurfaceMm: Vec3,
  offsetMm: Vec3,
): Vec3 {
  return [
    pointerSurfaceMm[0] + offsetMm[0],
    pointerSurfaceMm[1] + offsetMm[1],
    pointerSurfaceMm[2] + offsetMm[2],
  ];
}

export function resolveElementResize(
  element: SceneElement,
  item: CatalogItem | null,
  gizmoScale: Vec3,
): ElementResize {
  const dimensionsMm = constrainedDimensions(element, item, gizmoScale);

  const originalHeight = element.dimensionsMm.height * Math.abs(element.transform.scale[1]);
  const nextHeight = dimensionsMm.height * Math.abs(element.transform.scale[1]);
  return {
    dimensionsMm,
    translationMm: [
      element.transform.translationMm[0],
      finiteMillimeter(element.transform.translationMm[1] - originalHeight / 2 + nextHeight / 2),
      element.transform.translationMm[2],
    ],
  };
}

/**
 * Converts Babylon's mesh scale into a scale relative to the persisted element.
 * Imported meshes keep an internal/base scale between edits, so using their
 * absolute scale directly makes a second resize start with a stale multiplier.
 */
export function resolveRelativeGizmoScale(
  elementScale: Vec3,
  meshScaleAtDragStart: Vec3,
  currentMeshScale: Vec3,
): Vec3 {
  return elementScale.map((scale, index) => {
    const start = Math.max(1e-6, Math.abs(meshScaleAtDragStart[index]!));
    const current = Math.max(1e-6, Math.abs(currentMeshScale[index]!));
    return scale * current / start;
  }) as Vec3;
}

/** The resize rules for one axis, from the catalog or from the placed copy. */
export function readResizeAxis(
  element: SceneElement,
  item: CatalogItem | null,
  dimension: "width" | "height" | "depth",
): ResizeAxis | null {
  const embedded = element.parameters?.resize as CatalogItem["resize"] | null | undefined;
  return item?.resize?.[dimension] ?? embedded?.[dimension] ?? null;
}

/**
 * Rounds a size that a collision cut short back onto the step, always away from
 * whatever stopped it.
 *
 * The drag stops the face on the surface it met, at whatever fraction of a
 * millimetre that happens to be; the commit then rounds to the nearest step,
 * and half the time the nearest step is a few millimetres further in. The
 * module ends up genuinely embedded in its neighbour — and from there every
 * later resize of it starts from a state that is already colliding.
 */
export function snapCollisionStoppedDimensionMm(
  stoppedMm: number,
  startMm: number,
  axis: ResizeAxis | null,
): number {
  const step = axis?.enabled ? axis.stepMm : 0;
  if (!(step > 0) || stoppedMm === startMm) return stoppedMm;
  const origin = axis?.minMm ?? startMm;
  const offset = (stoppedMm - origin) / step;
  const steps = stoppedMm > startMm ? Math.floor(offset) : Math.ceil(offset);
  return Math.max(MIN_DIMENSION_MM, finiteMillimeter(origin + steps * step));
}

/**
 * Turns the size a face drag is asking for into the gizmo scale that produces
 * it. The floor is not politeness: a dimension below zero becomes a negative
 * scale, whose modulus the resize takes, and the face reappears mirrored on the
 * far side of the component — flipping every frame the hand shakes around zero.
 */
export function resolveFaceResizeGizmoScale(
  element: SceneElement,
  axisIndex: 0 | 1 | 2,
  requestedDimensionMm: number,
): Vec3 {
  const dimension = (["width", "height", "depth"] as const)[axisIndex];
  const scale = [...element.transform.scale] as Vec3;
  scale[axisIndex] = element.transform.scale[axisIndex]!
    * Math.max(MIN_DIMENSION_MM, requestedDimensionMm)
    / Math.max(1, element.dimensionsMm[dimension]);
  return scale;
}

/**
 * Converts one pointer movement into a dimension change along the projected
 * resize axis. Callers accumulate onto their own previous *request*, never onto
 * the size that survived the step, the catalog and the room — so a movement
 * beyond a limit is remembered and has to be given back before the face follows
 * the hand again, and the arrow never drifts away from the cursor.
 */
export function resolveIncrementalFaceResizeDimension(
  currentDimensionMm: number,
  pointerDeltaPx: readonly [number, number],
  projectedMeterAxisPx: readonly [number, number],
): number {
  const axisLengthSquared =
    projectedMeterAxisPx[0] * projectedMeterAxisPx[0]
    + projectedMeterAxisPx[1] * projectedMeterAxisPx[1];
  if (!Number.isFinite(axisLengthSquared) || axisLengthSquared < 1e-6) {
    return currentDimensionMm;
  }
  const projectedMeters = (
    pointerDeltaPx[0] * projectedMeterAxisPx[0]
    + pointerDeltaPx[1] * projectedMeterAxisPx[1]
  ) / axisLengthSquared;
  return finiteMillimeter(currentDimensionMm + projectedMeters * 1_000);
}

/**
 * Converts a one-sided face drag into catalog dimensions while keeping the
 * opposite face anchored. The upper height face preserves the base; the lower
 * face of a suspended element preserves the top.
 */
export function resolveElementFaceResize(
  element: SceneElement,
  item: CatalogItem | null,
  gizmoScale: Vec3,
  localDragAxis: Vec3,
  worldDragAxis: Vec3 = localDragAxis,
): ElementResize {
  const dimensionsMm = constrainedDimensions(element, item, gizmoScale);
  const axisIndex = dominantAxisIndex(localDragAxis);
  const dimensionKey = (["width", "height", "depth"] as const)[axisIndex];
  const originalTransformScale = Math.max(
    1e-6,
    Math.abs(element.transform.scale[axisIndex]!),
  );
  const originalRenderedSizeMm =
    element.dimensionsMm[dimensionKey] * originalTransformScale;
  const constrainedRenderedSizeMm =
    dimensionsMm[dimensionKey] * originalTransformScale;
  const normalizedWorldAxis = normalizeAxis(worldDragAxis, localDragAxis);
  const centerDisplacementMm =
    (constrainedRenderedSizeMm - originalRenderedSizeMm) / 2;
  const translationMm: Vec3 = [
    finiteMillimeter(element.transform.translationMm[0] + normalizedWorldAxis[0] * centerDisplacementMm),
    finiteMillimeter(element.transform.translationMm[1] + normalizedWorldAxis[1] * centerDisplacementMm),
    finiteMillimeter(element.transform.translationMm[2] + normalizedWorldAxis[2] * centerDisplacementMm),
  ];

  if (axisIndex === 1) {
    const originalRenderedHeight =
      element.dimensionsMm.height * Math.abs(element.transform.scale[1]);
    const originalBaseMm = element.transform.translationMm[1] - originalRenderedHeight / 2;
    const nextRenderedHeight = dimensionsMm.height * Math.abs(element.transform.scale[1]);
    const originalTopMm = element.transform.translationMm[1] + originalRenderedHeight / 2;
    translationMm[1] = normalizedWorldAxis[1] < 0
      ? finiteMillimeter(originalTopMm - nextRenderedHeight / 2)
      : finiteMillimeter(originalBaseMm + nextRenderedHeight / 2);
  }

  return { dimensionsMm, translationMm };
}

/**
 * Stops a one-sided resize at the first physical boundary it reaches: a wall,
 * the ceiling or a neighbouring component. Since an imported component declares
 * no size of its own, this is the only limit a resize normally meets — and
 * `bypass`, the same Ctrl gesture that lets a move cross what is in its way,
 * is how the user says they mean to go through it anyway.
 */
export function resolveCollisionConstrainedFaceResize(
  scene: SceneDocument,
  element: SceneElement,
  target: ElementResize,
  rotationRad: Vec3 = element.transform.rotationRad,
  options: ResizeCollisionConstraintOptions = {},
): CollisionConstrainedResize {
  if (options.bypass) {
    return { ...target, collisionKind: null, collisionId: null };
  }
  const start: ElementResize = {
    dimensionsMm: { ...element.dimensionsMm },
    translationMm: [...element.transform.translationMm],
  };
  // The search below walks out from the component's current size and stops at
  // the first candidate that hits something, which takes for granted that the
  // current size hits nothing. When it already does, every candidate collides,
  // the walk collapses back to the start, and the component becomes impossible
  // to resize at all — not even smaller, away from what it is touching.
  //
  // So whatever it is already inside is set aside, by name, for this drag only.
  // Waiving the limits wholesale instead — which is what the first attempt at
  // this did — let a module that had ended up across one wall grow through
  // every other wall and three neighbours without ever reporting a limit.
  const ignore = collisionsAlreadyPresent(scene, element, start, rotationRad, options.surface ?? null);
  const targetCollision = findResizeCollision(
    scene,
    element,
    target,
    rotationRad,
    options.surface ?? null,
    ignore,
  );
  if (!targetCollision) {
    return {
      ...target,
      collisionKind: null,
      collisionId: null,
    };
  }

  const dimensionDelta = Math.max(
    Math.abs(target.dimensionsMm.width - start.dimensionsMm.width),
    Math.abs(target.dimensionsMm.height - start.dimensionsMm.height),
    Math.abs(target.dimensionsMm.depth - start.dimensionsMm.depth),
  );
  const translationDelta = Math.hypot(
    target.translationMm[0] - start.translationMm[0],
    target.translationMm[1] - start.translationMm[1],
    target.translationMm[2] - start.translationMm[2],
  );
  const steps = Math.max(1, Math.min(320, Math.ceil(
    Math.max(dimensionDelta, translationDelta) / 50,
  )));
  // The target is known to collide, so it is the upper bound from the outset and
  // is never sampled as if it might be clear. It used to be: the walk ran up to
  // and including t = 1, and interpolating all the way there does not reproduce
  // the target bit for bit — a hair under the half-millimetre tolerance was
  // enough for the last sample to read as clear, and the function then returned
  // the full target carrying a collision label. The face reported itself blocked
  // and went straight through, leaving the module buried in its neighbour.
  let clearT = 0;
  let blockedT = 1;
  let collision = targetCollision;

  for (let step = 1; step < steps; step += 1) {
    const t = step / steps;
    const candidateCollision = findResizeCollision(
      scene,
      element,
      interpolateResize(start, target, t),
      rotationRad,
      options.surface ?? null,
      ignore,
    );
    if (!candidateCollision) {
      clearT = t;
      continue;
    }
    collision = candidateCollision;
    blockedT = t;
    break;
  }

  for (let iteration = 0; iteration < 20; iteration += 1) {
    const middle = (clearT + blockedT) / 2;
    if (
      findResizeCollision(
        scene,
        element,
        interpolateResize(start, target, middle),
        rotationRad,
        options.surface ?? null,
        ignore,
      )
    ) {
      blockedT = middle;
    } else {
      clearT = middle;
    }
  }

  return {
    ...interpolateResize(start, target, clearT),
    collisionKind: collision.kind,
    collisionId: collision.id,
  };
}

function constrainedDimensions(
  element: SceneElement,
  item: CatalogItem | null,
  gizmoScale: Vec3,
): SceneElement["dimensionsMm"] {
  const keys = ["width", "height", "depth"] as const;
  const dimensionsMm = { ...element.dimensionsMm };
  const embeddedResize = element.parameters?.resize as CatalogItem["resize"] | null | undefined;
  keys.forEach((key, index) => {
    const originalScale = Math.max(1e-6, Math.abs(element.transform.scale[index]!));
    const requested = element.dimensionsMm[key] * Math.abs(gizmoScale[index]!) / originalScale;
    const constraint = item?.resize?.[key] ?? embeddedResize?.[key];
    dimensionsMm[key] = constraint
      ? constraint.enabled
        ? quantizeDimension(
            requested,
            constraint.minMm,
            constraint.maxMm,
            constraint.stepMm,
            element.dimensionsMm[key],
          )
        : element.dimensionsMm[key]
      : Math.max(MIN_DIMENSION_MM, finiteMillimeter(requested));
  });
  return dimensionsMm;
}

function dominantAxisIndex(axis: Vec3): 0 | 1 | 2 {
  const magnitudes = axis.map((value) => Math.abs(value));
  if (magnitudes[1]! >= magnitudes[0]! && magnitudes[1]! >= magnitudes[2]!) return 1;
  if (magnitudes[2]! >= magnitudes[0]!) return 2;
  return 0;
}

function normalizeAxis(axis: Vec3, fallback: Vec3): Vec3 {
  const length = Math.hypot(...axis);
  if (length > 1e-6) {
    return [axis[0] / length, axis[1] / length, axis[2] / length];
  }
  const fallbackLength = Math.max(1e-6, Math.hypot(...fallback));
  return [
    fallback[0] / fallbackLength,
    fallback[1] / fallbackLength,
    fallback[2] / fallbackLength,
  ];
}

function normalizeHorizontal(axis: Vec3): Vec3 {
  const length = Math.hypot(axis[0], axis[2]);
  if (length > 1e-6) return [axis[0] / length, 0, axis[2] / length];
  return [0, 0, 1];
}

function projectPointToPlane(point: Vec3, planePoint: Vec3, planeNormal: Vec3): Vec3 {
  const distance =
    (point[0] - planePoint[0]) * planeNormal[0] +
    (point[1] - planePoint[1]) * planeNormal[1] +
    (point[2] - planePoint[2]) * planeNormal[2];
  return [
    point[0] - planeNormal[0] * distance,
    point[1] - planeNormal[1] * distance,
    point[2] - planeNormal[2] * distance,
  ];
}

interface HorizontalObb {
  center: [number, number];
  axes: [[number, number], [number, number]];
  halfSize: [number, number];
  bottomMm: number;
  topMm: number;
}

const EMPTY_IGNORE: ReadonlySet<string> = new Set();

/**
 * Everything the component is already inside before the drag starts, by name.
 * The first hit hides the rest, so the probe repeats with each one set aside
 * until nothing is left; a handful of passes is far more than a real room needs.
 */
function collisionsAlreadyPresent(
  scene: SceneDocument,
  element: SceneElement,
  start: ElementResize,
  rotationRad: Vec3,
  surface: PlacementSurfaceConstraint | null,
): ReadonlySet<string> {
  const ignore = new Set<string>();
  for (let pass = 0; pass < 8; pass += 1) {
    const hit = findResizeCollision(scene, element, start, rotationRad, surface, ignore);
    if (!hit || ignore.has(hit.id)) break;
    ignore.add(hit.id);
  }
  return ignore;
}

function findResizeCollision(
  scene: SceneDocument,
  element: SceneElement,
  resize: ElementResize,
  rotationRad: Vec3,
  surface: PlacementSurfaceConstraint | null,
  ignore: ReadonlySet<string> = EMPTY_IGNORE,
): { kind: ResizeCollisionKind; id: string } | null {
  const renderedSizeMm: Vec3 = [
    resize.dimensionsMm.width * Math.abs(element.transform.scale[0]),
    resize.dimensionsMm.height * Math.abs(element.transform.scale[1]),
    resize.dimensionsMm.depth * Math.abs(element.transform.scale[2]),
  ];

  const surfaceBoundary = findPlacementSurfaceBoundaryCollision(
    scene,
    resize.translationMm,
    renderedSizeMm,
    rotationRad,
    surface,
  );
  if (surfaceBoundary && !ignore.has(surfaceBoundary.id)) return surfaceBoundary;

  const bottomMm = resize.translationMm[1] - renderedSizeMm[1] / 2;
  const topMm = resize.translationMm[1] + renderedSizeMm[1] / 2;
  const containingLevel = [...scene.levels]
    .sort((left, right) => left.elevationMm - right.elevationMm)
    .find((level) => (
      bottomMm >= level.elevationMm - 0.5
      && bottomMm <= level.elevationMm + level.heightMm + 0.5
    ));
  const level = containingLevel ?? scene.levels[0];
  if (level && topMm > level.elevationMm + level.heightMm + 0.05 && !ignore.has(level.id)) {
    return { kind: "ceiling", id: level.id };
  }

  return findPlacementCollision(
    scene,
    element.id,
    resize.translationMm,
    renderedSizeMm,
    rotationRad,
    ignore,
  );
}

function findPlacementSurfaceBoundaryCollision(
  scene: SceneDocument,
  positionMm: Vec3,
  renderedSizeMm: Vec3,
  rotationRad: Vec3,
  surface: PlacementSurfaceConstraint | null,
): { kind: "wall"; id: string } | null {
  if (surface?.kind !== "wall" || !surface.wallId) return null;
  const wall = scene.walls.find((candidate) => candidate.id === surface.wallId);
  if (!wall) return null;
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const length = Math.hypot(dx, dz);
  if (length < 1) return null;

  const tangent: [number, number] = [dx / length, dz / length];
  const footprint = createHorizontalObb(
    [positionMm[0], positionMm[2]],
    renderedSizeMm[0],
    renderedSizeMm[2],
    rotationRad[1],
    positionMm[1] - renderedSizeMm[1] / 2,
    positionMm[1] + renderedSizeMm[1] / 2,
  );
  const centerFromStart: [number, number] = [
    footprint.center[0] - wall.startMm[0],
    footprint.center[1] - wall.startMm[1],
  ];
  const centerAlongWall = dot2(centerFromStart, tangent);
  const halfAlongWall =
    footprint.halfSize[0] * Math.abs(dot2(footprint.axes[0], tangent))
    + footprint.halfSize[1] * Math.abs(dot2(footprint.axes[1], tangent));
  if (
    centerAlongWall - halfAlongWall < -0.05
    || centerAlongWall + halfAlongWall > length + 0.05
    || footprint.bottomMm < -0.05
    || footprint.topMm > wall.heightMm + 0.05
  ) {
    return { kind: "wall", id: wall.id };
  }
  return null;
}

function interpolateResize(start: ElementResize, target: ElementResize, t: number): ElementResize {
  return {
    dimensionsMm: {
      width: finiteMillimeter(
        start.dimensionsMm.width + (target.dimensionsMm.width - start.dimensionsMm.width) * t,
      ),
      height: finiteMillimeter(
        start.dimensionsMm.height + (target.dimensionsMm.height - start.dimensionsMm.height) * t,
      ),
      depth: finiteMillimeter(
        start.dimensionsMm.depth + (target.dimensionsMm.depth - start.dimensionsMm.depth) * t,
      ),
    },
    translationMm: interpolatePosition(start.translationMm, target.translationMm, t),
  };
}

function findPlacementCollision(
  scene: SceneDocument,
  elementId: string,
  positionMm: Vec3,
  renderedSizeMm: Vec3,
  rotationRad: Vec3,
  ignore: ReadonlySet<string> = EMPTY_IGNORE,
): { kind: PlacementCollisionKind; id: string } | null {
  const moving = createHorizontalObb(
    [positionMm[0], positionMm[2]],
    renderedSizeMm[0],
    renderedSizeMm[2],
    rotationRad[1],
    positionMm[1] - renderedSizeMm[1] / 2,
    positionMm[1] + renderedSizeMm[1] / 2,
  );

  for (const wall of scene.walls) {
    if (ignore.has(wall.id)) continue;
    if (!verticalIntervalsOverlap(moving.bottomMm, moving.topMm, 0, wall.heightMm)) continue;
    const dx = wall.endMm[0] - wall.startMm[0];
    const dz = wall.endMm[1] - wall.startMm[1];
    const length = Math.hypot(dx, dz);
    if (length < 1) continue;
    const tangent: [number, number] = [dx / length, dz / length];
    const normal: [number, number] = [-tangent[1], tangent[0]];
    const obstacle: HorizontalObb = {
      center: [
        (wall.startMm[0] + wall.endMm[0]) / 2,
        (wall.startMm[1] + wall.endMm[1]) / 2,
      ],
      axes: [tangent, normal],
      halfSize: [length / 2, Math.max(1, wall.thicknessMm) / 2],
      bottomMm: 0,
      topMm: wall.heightMm,
    };
    if (horizontalObbsOverlap(moving, obstacle)) return { kind: "wall", id: wall.id };
  }

  for (const element of scene.elements) {
    if (element.id === elementId || !element.visible || ignore.has(element.id)) continue;
    const sizeMm = renderedElementSizeMm(element);
    const centerMm = renderedElementCenterMm(element);
    const vertical = renderedElementVerticalIntervalMm(element);
    const obstacle = createHorizontalObb(
      [centerMm[0], centerMm[2]],
      sizeMm[0],
      sizeMm[2],
      element.transform.rotationRad[1],
      vertical[0],
      vertical[1],
    );
    if (!verticalIntervalsOverlap(moving.bottomMm, moving.topMm, obstacle.bottomMm, obstacle.topMm)) {
      continue;
    }
    if (horizontalObbsOverlap(moving, obstacle)) {
      return { kind: "element", id: element.id };
    }
  }

  return null;
}

function createHorizontalObb(
  center: [number, number],
  widthMm: number,
  depthMm: number,
  yawRad: number,
  bottomMm: number,
  topMm: number,
): HorizontalObb {
  const angle = Number.isFinite(yawRad) ? yawRad : 0;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  return {
    center,
    axes: [
      [cosine, -sine],
      [sine, cosine],
    ],
    halfSize: [
      Math.max(1, Math.abs(widthMm)) / 2,
      Math.max(1, Math.abs(depthMm)) / 2,
    ],
    bottomMm: Math.min(bottomMm, topMm),
    topMm: Math.max(bottomMm, topMm),
  };
}

function horizontalObbsOverlap(a: HorizontalObb, b: HorizontalObb): boolean {
  const centerDelta: [number, number] = [
    b.center[0] - a.center[0],
    b.center[1] - a.center[1],
  ];
  const axes = [...a.axes, ...b.axes];
  for (const axis of axes) {
    const centerDistance = Math.abs(dot2(centerDelta, axis));
    const radiusA = horizontalObbProjectionRadius(a, axis);
    const radiusB = horizontalObbProjectionRadius(b, axis);
    if (centerDistance >= radiusA + radiusB - 0.5) return false;
  }
  return true;
}

function horizontalObbProjectionRadius(
  box: HorizontalObb,
  axis: [number, number],
): number {
  return box.halfSize[0] * Math.abs(dot2(box.axes[0], axis))
    + box.halfSize[1] * Math.abs(dot2(box.axes[1], axis));
}

function verticalIntervalsOverlap(
  bottomA: number,
  topA: number,
  bottomB: number,
  topB: number,
): boolean {
  return Math.min(topA, topB) - Math.max(bottomA, bottomB) > 0.5;
}

function interpolatePosition(start: Vec3, target: Vec3, t: number): Vec3 {
  return [
    finiteMillimeter(start[0] + (target[0] - start[0]) * t),
    finiteMillimeter(start[1] + (target[1] - start[1]) * t),
    finiteMillimeter(start[2] + (target[2] - start[2]) * t),
  ];
}

function dot2(a: [number, number], b: [number, number]): number {
  return a[0] * b[0] + a[1] * b[1];
}

function dotHorizontal(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[2] * b[2];
}

function finiteVec3(value: unknown): Vec3 | null {
  if (
    !Array.isArray(value) ||
    value.length !== 3 ||
    value.some((component) => !Number.isFinite(component))
  ) {
    return null;
  }
  return [Number(value[0]), Number(value[1]), Number(value[2])];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function finiteAngle(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const normalized = ((value + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
  return Math.round(normalized * 1_000_000) / 1_000_000;
}

/**
 * An axis with no declared bound is only held back by MIN_DIMENSION_MM, which
 * is not a limit so much as the point where a part would turn inside out.
 *
 * The step is counted from the declared minimum when there is one and from the
 * component's own size when there is not — never from zero. An imported module
 * is 615 mm deep, not 610 or 620, and snapping to an absolute grid would drag
 * every dimension a few millimetres the moment any one of them was touched.
 */
function quantizeDimension(
  value: number,
  min: number | null,
  max: number | null,
  step: number,
  currentMm: number,
): number {
  const lower = min ?? MIN_DIMENSION_MM;
  const upper = max ?? Number.POSITIVE_INFINITY;
  const clamped = Math.min(upper, Math.max(lower, value));
  if (step <= 0) return finiteMillimeter(clamped);
  const origin = min ?? currentMm;
  return finiteMillimeter(
    Math.min(upper, Math.max(lower, origin + Math.round((clamped - origin) / step) * step)),
  );
}

function finiteMillimeter(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 10) / 10 : 0;
}
